import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatMoney, localIsoDate } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Checkbox, Detail, Field, Input, Select, Textarea, useToast } from '../components/ui';
import { useClientOptions } from './Invoices';
import {
  useExternalSystemOptions,
  usePackageOptions,
  useSubscriptionProjects,
  type Cycle,
  type DiscountType,
  type PackageOption,
  type SubscriptionDetail,
} from './Subscriptions';

/**
 * The subscription's forms (2026-10-28): making one, changing its agreement,
 * cancelling it — each a page, as every form in Finance is. The API works the
 * price and the first period out as the form is filled in (`/subscriptions/preview`)
 * and checks everything again on saving.
 */

interface Preview {
  ready: boolean;
  problems: Array<{ field: string; message: string }>;
  today: string;
  currency?: string;
  listPrice?: number | null;
  basePrice?: number | null;
  discountAmount?: number | null;
  netPrice?: number | null;
  customPrice?: boolean;
  firstPeriod?: { start: string; end: string } | null;
  effectiveFrom?: string | null;
  invoicedOnSave?: boolean;
  currencyNote?: { package: string; client: string; allowed: boolean } | null;
}

const listOf = (pkg: PackageOption | undefined, cycle: Cycle) =>
  !pkg ? null : cycle === 'MONTHLY' ? pkg.monthlyPrice : pkg.yearlyPrice;

/** The price fields every subscription form has: a price of its own, and a discount. */
function PriceFields({
  currency,
  list,
  custom,
  setCustom,
  basePrice,
  setBasePrice,
  discountType,
  setDiscountType,
  discountValue,
  setDiscountValue,
  problem,
}: {
  currency: string;
  list: number | null;
  custom: boolean;
  setCustom: (v: boolean) => void;
  basePrice: string;
  setBasePrice: (v: string) => void;
  discountType: DiscountType;
  setDiscountType: (v: DiscountType) => void;
  discountValue: string;
  setDiscountValue: (v: string) => void;
  problem: (field: string) => string | null;
}) {
  const { t, locale } = useI18n();
  return (
    <>
      <Field
        label={t('subs.form.agreedPrice')}
        hint={list === null ? undefined : t('subs.form.priceHint', { price: formatMoney(list, currency, locale) })}
        error={problem('basePrice')}
      >
        <div className="grid gap-2">
          <Checkbox checked={custom} onChange={(e) => setCustom(e.target.checked)} label={t('subs.form.customPrice')} />
          {custom && (
            <Input type="number" min={0} step="0.01" inputMode="decimal" value={basePrice} onChange={(e) => setBasePrice(e.target.value)} dir="ltr" />
          )}
        </div>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('subs.detail.discount')} error={problem('discountType')}>
          <Select value={discountType} onChange={(e) => setDiscountType(e.target.value as DiscountType)}>
            {(['NONE', 'PERCENT', 'FIXED'] as const).map((d) => (
              <option key={d} value={d}>{t(`subs.discount.${d}`)}</option>
            ))}
          </Select>
        </Field>
        <Field
          label={discountType === 'PERCENT' ? t('subs.form.discountPercent') : t('subs.form.discountAmount', { currency })}
          error={problem('discountValue')}
        >
          <Input
            type="number"
            min={0}
            max={discountType === 'PERCENT' ? 100 : undefined}
            step="0.01"
            inputMode="decimal"
            disabled={discountType === 'NONE'}
            value={discountType === 'NONE' ? '' : discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            dir="ltr"
          />
        </Field>
      </div>
    </>
  );
}

/** What the subscription comes to, as the API worked it out. */
function PreviewSummary({ preview, cycle, changing }: { preview: Preview | undefined; cycle: Cycle; changing?: boolean }) {
  const { t, locale } = useI18n();
  if (!preview?.currency) return <p className="text-body-sm text-muted">{t('subs.form.choosePackageFirst')}</p>;
  const money = (n: number | null | undefined) => (n === null || n === undefined ? '—' : formatMoney(n, preview.currency!, locale));
  return (
    <div>
      <p className="mb-2 text-overline uppercase text-[var(--text-tertiary)]">{t('subs.form.summary')}</p>
      <dl className="divide-y divide-[var(--line)]">
        <Detail label={t('subs.detail.listPrice')}>{money(preview.listPrice)}</Detail>
        <Detail label={t('subs.detail.basePrice')}>
          {money(preview.basePrice)}
          {preview.customPrice && <span className="ms-2 text-caption text-muted">{t('subs.form.ownPrice')}</span>}
        </Detail>
        <Detail label={t('subs.detail.discount')}>{money(preview.discountAmount)}</Detail>
        <Detail label={t('subs.detail.net')}>
          <span className="font-medium tabular">
            {preview.netPrice === null || preview.netPrice === undefined
              ? '—'
              : `${money(preview.netPrice)} ${t(`subs.per.${cycle}` as TranslationKey)}`}
          </span>
        </Detail>
        {preview.firstPeriod && (
          <Detail label={changing ? t('subs.terms.firstPeriod') : t('subs.form.firstPeriod')}>
            {formatDate(preview.firstPeriod.start, locale)} – {formatDate(preview.firstPeriod.end, locale)}
          </Detail>
        )}
      </dl>
      {!changing && preview.firstPeriod && (
        <p className="mt-3 text-body-sm">
          {preview.invoicedOnSave ? t('subs.form.invoicedNow') : t('subs.form.invoicedLater', { date: formatDate(preview.firstPeriod.start, locale) })}
        </p>
      )}
      {changing && preview.effectiveFrom && (
        <p className="mt-3 text-body-sm">{t('subs.terms.hint', { date: formatDate(preview.effectiveFrom, locale) })}</p>
      )}
      {preview.netPrice === 0 && <p className="mt-2 text-body-sm text-warning-fg dark:text-warning-dark">{t('subs.detail.free')}</p>}
      {preview.currencyNote && (
        <p className={`mt-2 text-body-sm ${preview.currencyNote.allowed ? 'text-muted' : 'text-error-fg dark:text-error-dark'}`}>
          {t('subs.form.currencyNote', { client: preview.currencyNote.client, package: preview.currencyNote.package })}{' '}
          {!preview.currencyNote.allowed && t('subs.form.currencyRefused')}
        </p>
      )}
      <p className="mt-2 text-caption text-faint">{t('subs.form.noTax')}</p>
    </div>
  );
}

/** A field's message from the preview's problems or the save's refusal. */
function useProblems(preview: Preview | undefined, saveError: unknown) {
  return (field: string): string | null => {
    const fromSave = saveError instanceof ApiError && (saveError.details as { field?: string } | undefined)?.field === field ? saveError.message : null;
    return fromSave ?? preview?.problems.find((p) => p.field === field)?.message ?? null;
  };
}

/** A number typed in a field, or undefined while it is empty. */
const numberOrUndefined = (text: string) => (text.trim() === '' ? undefined : Number(text));

export function SubscriptionFormPage() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const clients = useClientOptions();
  const projects = useSubscriptionProjects();
  const systems = useExternalSystemOptions();

  const [clientId, setClientId] = useState(params.get('clientId') ?? '');
  const [projectId, setProjectId] = useState(params.get('projectId') ?? '');
  const [packageId, setPackageId] = useState('');
  const [cycle, setCycle] = useState<Cycle>('MONTHLY');
  const [custom, setCustom] = useState(false);
  const [basePrice, setBasePrice] = useState('');
  const [discountType, setDiscountType] = useState<DiscountType>('NONE');
  const [discountValue, setDiscountValue] = useState('');
  const [startDate, setStartDate] = useState(localIsoDate());
  const [systemId, setSystemId] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [notes, setNotes] = useState('');

  const packages = usePackageOptions(projectId);
  const pkg = packages.data?.find((p) => p.id === packageId);
  const list = listOf(pkg, cycle);
  const offered = (projects.data ?? []).filter((p) => p.offered && !p.closed);
  const linkable = (systems.data ?? []).filter((s) => s.projectIds.includes(projectId));

  // A package that does not sell the cycle chosen moves the cycle to the one it sells.
  useEffect(() => {
    if (pkg && listOf(pkg, cycle) === null) setCycle(cycle === 'MONTHLY' ? 'YEARLY' : 'MONTHLY');
  }, [pkg, cycle]);
  useEffect(() => {
    if (!custom && list !== null) setBasePrice(String(list));
  }, [custom, list]);

  const body = useMemo(
    () => ({
      clientId: clientId || undefined,
      packageId: packageId || undefined,
      billingCycle: cycle,
      startDate: startDate || undefined,
      basePrice: custom ? numberOrUndefined(basePrice) : undefined,
      discountType,
      discountValue: discountType === 'NONE' ? undefined : numberOrUndefined(discountValue),
    }),
    [clientId, packageId, cycle, startDate, custom, basePrice, discountType, discountValue],
  );
  const preview = useQuery({
    queryKey: ['subscription-preview', body],
    queryFn: () => api.post<Preview>('/subscriptions/preview', body),
    enabled: !!packageId,
  });

  const save = useMutation({
    mutationFn: () =>
      api.post<SubscriptionDetail & { billed: Array<{ number: string }>; billingError: string | null }>('/subscriptions', {
        ...body,
        externalSystemId: systemId || undefined,
        externalRef: systemId && externalRef.trim() ? externalRef.trim() : undefined,
        notes: notes.trim() || undefined,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      void queryClient.invalidateQueries({ queryKey: ['subscription-projects'] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      if (created.billingError) toast.error(created.billingError);
      else if (created.billed.length) toast.success(t('subs.createdInvoiced', { number: created.billed.map((b) => b.number).join(', ') }));
      else toast.success(t('subs.created'));
      navigate(`/subscriptions/${created.id}`);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });
  const problem = useProblems(preview.data, save.error);
  const ready = !!clientId && !!packageId && preview.data?.ready === true && preview.data.currencyNote?.allowed !== false;

  return (
    <FormPage
      title={t('subs.new')}
      backLabel={t('subs.title')}
      submitLabel={t('subs.form.save')}
      onSubmit={() => save.mutate()}
      submitting={save.isPending}
      disabled={!ready}
      width="lg"
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="grid content-start gap-4">
          <FormGrid>
            <Field label={t('subs.client')} hint={t('subs.form.clientHint')} required error={problem('clientId')}>
              <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">{t('subs.form.chooseClient')}</option>
                {(clients.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {(locale === 'ar' && c.companyNameAr) || c.companyName} · {c.defaultCurrency}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('subs.product')} hint={t('subs.form.productHint')} required>
              <Select
                value={projectId}
                onChange={(e) => {
                  setProjectId(e.target.value);
                  setPackageId('');
                  setSystemId('');
                }}
              >
                <option value="">{t('subs.form.chooseProduct')}</option>
                {offered.map((p) => (
                  <option key={p.id} value={p.id}>{p.code} — {localized(p)}</option>
                ))}
              </Select>
            </Field>
            <Field label={t('subs.package')} required error={problem('packageId')}>
              <Select value={packageId} onChange={(e) => setPackageId(e.target.value)} disabled={!projectId}>
                <option value="">{t('subs.form.choosePackage')}</option>
                {(packages.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {localized(p)} · {p.currency}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('subs.cycle')} required error={problem('billingCycle')}>
              <Select value={cycle} onChange={(e) => setCycle(e.target.value as Cycle)}>
                {(['MONTHLY', 'YEARLY'] as const).map((c) => (
                  <option key={c} value={c} disabled={!!pkg && listOf(pkg, c) === null}>
                    {t(`subs.cycle.${c}`)}
                    {pkg && listOf(pkg, c) !== null ? ` — ${formatMoney(listOf(pkg, c)!, pkg.currency, locale)}` : pkg ? ` — ${t('subs.form.notSold')}` : ''}
                  </option>
                ))}
              </Select>
            </Field>
          </FormGrid>

          <FormGrid>
            <PriceFields
              currency={pkg?.currency ?? ''}
              list={list}
              custom={custom}
              setCustom={setCustom}
              basePrice={basePrice}
              setBasePrice={setBasePrice}
              discountType={discountType}
              setDiscountType={setDiscountType}
              discountValue={discountValue}
              setDiscountValue={setDiscountValue}
              problem={problem}
            />
          </FormGrid>

          <FormGrid>
            <Field label={t('subs.form.startDate')} hint={t('subs.form.startHint')} required error={problem('startDate')}>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
            <Field label={t('subs.form.system')} hint={t('subs.form.systemHint')} error={problem('externalSystemId')}>
              <Select value={systemId} onChange={(e) => setSystemId(e.target.value)} disabled={!projectId}>
                <option value="">{t('common.none')}</option>
                {linkable.map((s) => (
                  <option key={s.id} value={s.id} disabled={!s.isActive}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>
            {systemId && (
              <Field label={t('subs.form.externalRef')} hint={t('subs.form.externalRefHint')} error={problem('externalRef')}>
                <Input value={externalRef} onChange={(e) => setExternalRef(e.target.value)} dir="ltr" maxLength={200} />
              </Field>
            )}
          </FormGrid>

          <Field label={t('common.notes')}>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} dir="auto" />
          </Field>
        </div>

        <aside className="rounded-lg bg-[var(--surface-sunken)] p-4">
          <PreviewSummary preview={preview.data} cycle={cycle} />
        </aside>
      </div>
    </FormPage>
  );
}

/**
 * A change to the agreement: package, cycle, price or discount — from the
 * next period not yet invoiced — and its link to an external system.
 */
export function SubscriptionTermsPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const systems = useExternalSystemOptions();
  const { data: s, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription', id],
    queryFn: () => api.get<SubscriptionDetail>(`/subscriptions/${id}`),
  });

  const [packageId, setPackageId] = useState('');
  const [cycle, setCycle] = useState<Cycle>('MONTHLY');
  const [custom, setCustom] = useState(false);
  const [basePrice, setBasePrice] = useState('');
  const [discountType, setDiscountType] = useState<DiscountType>('NONE');
  const [discountValue, setDiscountValue] = useState('');
  const [systemId, setSystemId] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!s || loaded) return;
    setPackageId(s.package.id);
    setCycle(s.billingCycle);
    setBasePrice(String(s.basePrice));
    setDiscountType(s.discountType);
    setDiscountValue(s.discountType === 'NONE' ? '' : String(s.discountValue));
    setSystemId(s.externalSystem?.id ?? '');
    setExternalRef(s.externalRef ?? '');
    setNotes(s.notes ?? '');
    setLoaded(true);
  }, [s, loaded]);

  const packages = usePackageOptions(s?.project.id ?? '');
  const options = useMemo(() => {
    const active = packages.data ?? [];
    // An archived package it is on stays in the list, so the form can show it.
    return s && !active.some((p) => p.id === s.package.id)
      ? [{ ...s.package, currency: s.currency, monthlyPrice: null, yearlyPrice: null }, ...active]
      : active;
  }, [packages.data, s]);
  const pkg = options.find((p) => p.id === packageId);
  const repriced = !!s && (packageId !== s.package.id || cycle !== s.billingCycle);
  const list = listOf(pkg, cycle);

  useEffect(() => {
    if (loaded && repriced && !custom && list !== null) setBasePrice(String(list));
  }, [loaded, repriced, custom, list]);

  const termsBody = useMemo(() => {
    if (!s) return null;
    return {
      packageId: packageId !== s.package.id ? packageId : undefined,
      billingCycle: cycle !== s.billingCycle ? cycle : undefined,
      basePrice: custom || !repriced ? numberOrUndefined(basePrice) : undefined,
      discountType,
      discountValue: discountType === 'NONE' ? 0 : numberOrUndefined(discountValue),
    };
  }, [s, packageId, cycle, custom, repriced, basePrice, discountType, discountValue]);

  const preview = useQuery({
    queryKey: ['subscription-preview', id, termsBody],
    queryFn: () => api.post<Preview>('/subscriptions/preview', { subscriptionId: id, ...termsBody }),
    enabled: !!termsBody && !!packageId,
  });

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/subscriptions/${id}`, {
        ...termsBody,
        externalSystemId: systemId === (s?.externalSystem?.id ?? '') ? undefined : systemId || null,
        externalRef: externalRef.trim() === (s?.externalRef ?? '') ? undefined : externalRef.trim() || null,
        notes: notes === (s?.notes ?? '') ? undefined : notes,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subscription', id] });
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      toast.success(t('subs.terms.saved'));
      navigate(`/subscriptions/${id}`);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });
  const problem = useProblems(preview.data, save.error);
  const linkable = (systems.data ?? []).filter((x) => s && x.projectIds.includes(s.project.id));
  const fromApi = s?.source === 'API';

  return (
    <FormPage
      title={s ? t('subs.terms.title', { number: s.number }) : t('subs.detail.changeTerms')}
      subtitle={s ? `${localized(s.project)} · ${localized(s.package)}` : undefined}
      backTo={`/subscriptions/${id}`}
      backLabel={s?.number ?? t('subs.title')}
      submitLabel={t('subs.terms.save')}
      onSubmit={() => save.mutate()}
      submitting={save.isPending}
      disabled={!can(P.SUBSCRIPTIONS_MANAGE) || (preview.data !== undefined && !preview.data.ready)}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
      width="lg"
    >
      {s && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="grid content-start gap-4">
            <FormGrid>
              <Field label={t('subs.package')} hint={t('subs.terms.packageHint')} error={problem('packageId')}>
                <Select value={packageId} onChange={(e) => setPackageId(e.target.value)}>
                  {options
                    .filter((p) => p.currency === s.currency)
                    .map((p) => (
                      <option key={p.id} value={p.id}>{localized(p)}</option>
                    ))}
                </Select>
              </Field>
              <Field label={t('subs.cycle')} hint={cycle !== s.billingCycle && preview.data?.effectiveFrom ? t('subs.terms.cycleRestarts', { date: formatDate(preview.data.effectiveFrom, locale) }) : undefined} error={problem('billingCycle')}>
                <Select value={cycle} onChange={(e) => setCycle(e.target.value as Cycle)}>
                  {(['MONTHLY', 'YEARLY'] as const).map((c) => (
                    <option key={c} value={c}>{t(`subs.cycle.${c}`)}</option>
                  ))}
                </Select>
              </Field>
            </FormGrid>
            <FormGrid>
              <PriceFields
                currency={s.currency}
                list={list}
                custom={custom || !repriced}
                setCustom={setCustom}
                basePrice={basePrice}
                setBasePrice={setBasePrice}
                discountType={discountType}
                setDiscountType={setDiscountType}
                discountValue={discountValue}
                setDiscountValue={setDiscountValue}
                problem={problem}
              />
            </FormGrid>
            <FormGrid>
              <Field
                label={t('subs.form.system')}
                hint={fromApi ? t('subs.terms.systemFixed') : t('subs.form.systemHint')}
                error={problem('externalSystemId')}
              >
                <Select value={systemId} onChange={(e) => setSystemId(e.target.value)} disabled={fromApi}>
                  <option value="">{t('common.none')}</option>
                  {linkable.map((x) => (
                    <option key={x.id} value={x.id}>{x.name}</option>
                  ))}
                </Select>
              </Field>
              {systemId && (
                <Field label={t('subs.form.externalRef')} hint={t('subs.form.externalRefHint')} error={problem('externalRef')}>
                  <Input value={externalRef} onChange={(e) => setExternalRef(e.target.value)} dir="ltr" maxLength={200} />
                </Field>
              )}
            </FormGrid>
            <Field label={t('common.notes')}>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} dir="auto" />
            </Field>
            <Field label={t('subs.terms.reason')} hint={t('subs.terms.reasonHint')}>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} dir="auto" />
            </Field>
          </div>
          <aside className="rounded-lg bg-[var(--surface-sunken)] p-4">
            <PreviewSummary preview={preview.data} cycle={cycle} changing />
          </aside>
        </div>
      )}
    </FormPage>
  );
}

function Choice({
  checked,
  onChange,
  title,
  hint,
}: {
  checked: boolean;
  onChange: () => void;
  title: ReactNode;
  hint: ReactNode;
}) {
  return (
    <label
      className={`flex cursor-pointer gap-3 rounded-lg border p-4 transition-colors duration-fast ${
        checked ? 'border-primary bg-primary/5' : 'hairline hover:border-primary/40'
      }`}
    >
      <input type="radio" className="mt-1 accent-primary" checked={checked} onChange={onChange} />
      <span>
        <span className="block text-body font-medium">{title}</span>
        <span className="mt-0.5 block text-body-sm text-muted">{hint}</span>
      </span>
    </label>
  );
}

/** Cancel, at once or at the end of the period invoiced — a page, with its reason. */
export function SubscriptionCancelPage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'NOW' | 'PERIOD_END'>('PERIOD_END');
  const [reason, setReason] = useState('');
  const { data: s, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription', id],
    queryFn: () => api.get<SubscriptionDetail>(`/subscriptions/${id}`),
  });
  const today = localIsoDate();
  const runsTo = s?.currentPeriodEnd && s.currentPeriodEnd >= today ? s.currentPeriodEnd : null;

  const cancel = useMutation({
    mutationFn: () =>
      api.post<SubscriptionDetail & { openInvoices: Array<{ number: string }> }>(`/subscriptions/${id}/cancel`, {
        mode,
        reason: reason.trim() || undefined,
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['subscription', id] });
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      toast.success(result.status === 'CANCELLED' ? t('subs.cancel.done') : t('subs.cancel.scheduled', { date: formatDate(result.endsOn, locale) }));
      if (result.openInvoices.length) toast.success(t('subs.cancel.openInvoices', { list: result.openInvoices.map((i) => i.number).join(', ') }));
      navigate(`/subscriptions/${id}`);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });

  return (
    <FormPage
      title={s ? t('subs.cancel.title', { number: s.number }) : t('subs.detail.cancel')}
      backTo={`/subscriptions/${id}`}
      backLabel={s?.number ?? t('subs.title')}
      submitLabel={t('subs.cancel.submit')}
      onSubmit={() => cancel.mutate()}
      submitting={cancel.isPending}
      destructive
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
    >
      <div className="grid gap-3">
        <Choice
          checked={mode === 'PERIOD_END'}
          onChange={() => setMode('PERIOD_END')}
          title={t('subs.cancel.periodEnd')}
          hint={runsTo ? t('subs.cancel.periodEndHint', { date: formatDate(runsTo, locale) }) : t('subs.cancel.periodEndNone')}
        />
        <Choice checked={mode === 'NOW'} onChange={() => setMode('NOW')} title={t('subs.cancel.now')} hint={t('subs.cancel.nowHint')} />
        <p className="text-body-sm text-muted">{t('subs.cancel.invoicesStay')}</p>
        <Field label={t('common.reason')} hint={t('subs.cancel.reasonHint')}>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} dir="auto" />
        </Field>
      </div>
    </FormPage>
  );
}
